// Core message interfaces for chat functionality

export interface Base64ContentBlock {
  type: "image" | "document" | "audio" | "video" | "file";
  source_type: "base64";
  mime_type: string;
  data: string;
  thumbnail?: string; // Base64-encoded JPEG thumbnail for fast preview

  // Processing state (optional - only present during upload for instant preview)
  file?: File; // Original file for object URL instant preview
  processing?: boolean; // True while processing, removed when done
  processingStage?: "optimizing" | "validating" | "encoding" | "thumbnailing";
  id?: string; // Pre-generated message ID for Firestore (stable through entire lifecycle)

  metadata?: {
    name?: string;
    size?: number;
    description?: string; // User-provided description for the image
    descriptionAudioUrls?: string[]; // Audio URLs related to the description
    [key: string]: any;
  };
}

export interface StorageAudioBlock {
  type: "audio";
  source_type: "storage";
  mime_type: string; // e.g., "audio/webm", "audio/mpeg"
  relative_gcs_path: string; // Relative path in storage, e.g., "audio/1734002024528_audio.webm"
  fileName?: string; // Optional - extracted from relative_gcs_path if not provided
  metadata?: {
    duration?: number; // Optional - not always present from Firestore, will be loaded from audio element
    [key: string]: any;
  };
  // Transcript (optional - added by backend after transcription)
  transcript_text?: string;
  // Upload state (optional - only present during upload)
  uploading?: boolean;
  uploadProgress?: number; // 0-100 percentage
  // Firestore message creation state (optional - true while createCaptureContentMessage is in progress)
  creatingMessage?: boolean;
  // Firestore message ID (optional - set when saved in capture mode)
  messageId?: string;
}

// Storage Image Block - Frontend format only
export interface StorageImageBlock {
  type: "image";
  source_type: "storage";
  mime_type: string; // e.g., "image/jpeg", "image/png"
  fileName: string; // Sanitized filename, e.g., "1734002024528_photo.jpg"
  relative_gcs_path: string; // Relative path like "images/filename.png" (without userId/projectId prefix)

  // Optional fields
  thumbnail?: string; // Base64-encoded JPEG thumbnail for instant preview
  metadata?: {
    name?: string; // Original file name
    size?: number; // Original file size
    description?: string; // User-provided description for the image
    descriptionAudioUrls?: string[]; // Audio URLs related to the description
    [key: string]: any;
  };

  // Upload state (optional - only present during upload)
  uploading?: boolean;
  uploadProgress?: number; // 0-100 percentage
  // Firestore message creation state (optional - true while createCaptureContentMessage is in progress)
  creatingMessage?: boolean;

  // Firestore message ID (optional - set when saved in capture mode)
  messageId?: string;
  // Pre-generated message ID for Firestore (stable through entire lifecycle, same as Firestore doc ID)
  id?: string;
}

// Helper to extract fileName (now simply returns the fileName field)
export function getImageFileName(block: StorageImageBlock): string {
  return block.fileName;
}

export interface StoragePdfBlock {
  type: "file";
  source_type: "storage";
  mime_type: "application/pdf";
  fileName: string; // Sanitized filename, e.g., "1734002024528_document.pdf"
  metadata: {
    filename?: string; // Original file name
    name?: string; // Alternative name field
    size?: number; // Original file size
    [key: string]: any;
  };
  thumbnail?: string; // Base64-encoded JPEG thumbnail for preview
  // Upload state (optional - only present during upload)
  uploading?: boolean;
  uploadProgress?: number; // 0-100 percentage
  // Firestore message creation state (optional - true while createCaptureContentMessage is in progress)
  creatingMessage?: boolean;
  // Firestore message ID (optional - set when saved in capture mode)
  messageId?: string;
  // Pre-generated message ID for Firestore (stable through entire lifecycle, same as Firestore doc ID)
  id?: string;
}

interface DocumentContentBlock {
  type: "document";
  source: {
    type: "text" | "base64" | "storage";
    media_type?: string;
    data?: string; // For text: transcript content; For base64: encoded file data
    data_url?: string; // GCS path to audio file for UI playback (capture mode audio)
    relative_gcs_path?: string;
  };
  title?: string;
  citations?: any;
  context?: string;
  thumbnail?: string;

  // Audio metadata for reconstructing audio player from transcript documents
  // Used when loading messages from Firestore - stored in metadata.audio
  // Provides all info needed for playback: {gcs_path, fileName, duration, mime_type}
  metadata?: {
    audio?: {
      gcs_path?: string;
      fileName?: string;
      duration?: number;
      mime_type?: string;
    };
    image_ref?: string;
    [key: string]: any;
  };
}

// Union type for all content blocks
export type ContentBlock =
  | Base64ContentBlock
  | StorageAudioBlock
  | StorageImageBlock
  | StoragePdfBlock
  | DocumentContentBlock;

interface MessageContentText {
  type: "text";
  text: string;
}

interface MessageContentImage {
  type: "image";
  source_type: "base64";
  mime_type: string;
  data: string;
  thumbnail?: string; // Base64-encoded JPEG thumbnail for fast preview
}

interface MessageContentImageUrl {
  type: "image_url";
  image_url:
    | string
    | {
        url: string;
        detail?: "auto" | "low" | "high";
      };
}

interface MessageContentComplex {
  type: "text" | "image" | "tool_use" | "tool_result" | "image_url" | "thinking";
  text?: string;
  thinking?: string; // Thinking content for type: "thinking"
  signature?: string; // Cryptographic signature for thinking blocks
  source_type?: "base64";
  mime_type?: string;
  data?: string;
  tool_use_id?: string;
  name?: string;
  input?: any;
  content?: any;
  is_error?: boolean;
  image_url?:
    | string
    | {
        url: string;
        detail?: "auto" | "low" | "high";
      };
  id?: string;
}

type MessageContent =
  | MessageContentText
  | MessageContentImage
  | MessageContentImageUrl
  | MessageContentComplex
  | DocumentContentBlock;

interface MessageStatus {
  status: string;
  message: string;
  metadata?: Record<string, any>;
}

export interface BaseMessage {
  // Anthropic API fields
  role?: "user" | "assistant";
  content?: string | MessageContent[];

  // Legacy fields (for backward compatibility with non-API messages)
  // Note: Only "human", "ai", "system", and "status" are valid types in two-role model
  // Tool interactions are handled via content blocks (tool_use, tool_result) within messages
  id?: string;
  type?: "human" | "ai" | "system" | "status";

  // Session metadata (prefixed with _ in Firestore)
  created_at?: any;

  // Additional fields
  additional_kwargs?: Record<string, any>;
  response_metadata?: Record<string, any>;
  tool_calls?: ToolCall[];
  invalid_tool_calls?: ToolCall[];
  citations?: any[]; // Citations from Anthropic PDF/document processing

  // Status-specific fields (when type === 'status')
  status?: string | MessageStatus;
  message?: string;
  metadata?: Record<string, any>;
  status_history?: MessageStatus[];
}

interface ToolCall {
  id?: string;
  name: string;
  args: Record<string, any>;
  type?: "function" | "tool_call";
}

/**
 * ToolInputMessage is used as a prop type for the ToolInput UI component.
 * NOT stored in Firestore - HITL state is stored in tool_use content blocks within AssistantMessage.
 * This is constructed in-memory when rendering tool use blocks.
 *
 * Note: Does not extend BaseMessage as it's not part of the two-role model.
 */
export interface ToolInputMessage {
  id?: string;
  type: "tool_input";
  tool_name: string;
  tool_use_id: string;
  input_snapshot: Record<string, any>;
  is_complete: boolean;
  timestamp?: string;
  // Human-in-the-loop fields (now stored in tool_use content blocks)
  requires_approval?: boolean;
  approval_status?: "pending" | "approved" | "rejected" | "edited";
}

/**
 * ToolResultMessage is used as a prop type for the ToolResult UI component.
 * NOT stored in Firestore - tool results are stored as content blocks within UserMessage.
 * This is constructed in-memory from tool_result content blocks when rendering.
 *
 * Note: Does not extend BaseMessage as it's not part of the two-role model.
 */
export interface ToolResultMessage {
  id?: string;
  type: "tool_result";
  tool_name: string;
  tool_use_id: string;
  output: string;
  is_error: boolean;
  timestamp?: string;
}
